import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import { alpha } from '@mui/material/styles';
import type { Components, Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import * as React from 'react';
import { typographyVariantMappings } from './createTypography';

/**
 *
 * @param theme - this is the global theme w/o MUI components
 * @returns MUI component structure, pass to create stream in components object
 *
 * Note: You can use MUI theme in styleOverrides by providing a function for the key value which takes
 *
 */
const CreateMuiComponents = (theme: Theme): Components<Theme> => {
  const { palette } = theme;
  return {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2),
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2),
          '&:last-child': {
            paddingBottom: theme.spacing(2),
          },
        },
      },
    },
    MuiAvatar: {
      variants: [
        {
          props: { variant: 'rounded' },
          style: {
            border: `1px solid ${theme.palette.border.light}`,
            borderRadius: 8,
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
          },
        },
      ],
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          padding: theme.spacing(1, 2),
        },
        standardSuccess: {
          color: theme.palette.success.dark,
          backgroundColor: theme.palette.success.light,
        },
        standardInfo: {
          color: theme.palette.info.dark,
          backgroundColor: theme.palette.info.light,
        },
        standardWarning: {
          color: theme.palette.warning.dark,
          backgroundColor: theme.palette.warning.light,
        },
        standardError: {
          color: theme.palette.error.dark,
          backgroundColor: theme.palette.error.light,
        },
        filledSuccess: {
          color: theme.palette.success.contrastText,
          backgroundColor: theme.palette.success.main,
        },
        filledInfo: {
          color: theme.palette.info.contrastText,
          backgroundColor: theme.palette.info.main,
        },
        filledWarning: {
          color: theme.palette.warning.contrastText,
          backgroundColor: theme.palette.warning.main,
        },
        filledError: {
          color: theme.palette.error.contrastText,
          backgroundColor: theme.palette.error.main,
        },
        outlinedSuccess: {
          color: theme.palette.success.dark,
          borderColor: theme.palette.success.main,
        },
        outlinedInfo: {
          color: theme.palette.info.dark,
          borderColor: theme.palette.info.main,
        },
        outlinedWarning: {
          color: theme.palette.warning.dark,
          borderColor: theme.palette.warning.main,
        },
        outlinedError: {
          color: theme.palette.error.dark,
          borderColor: theme.palette.error.main,
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ArrowDropDownRoundedIcon />,
      },
      styleOverrides: {
        endAdornment: {
          marginRight: 6,
        },
        listbox: {
          padding: 8,
        },
        option: {
          borderRadius: 8,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          position: 'absolute',
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          backgroundColor: theme.palette.backdrop.light,
          // Default to true if blueEffect is not specified
          ...(ownerState.blurEffect !== false && {
            backdropFilter: theme.palette.blur.soft,
          }),
        }),
        invisible: {
          backgroundColor: 'transparent',
          backdropFilter: 'none',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        firstButton: {
          borderStyle: 'hidden',
          borderRadius: 'inherit',
          margin: 2,
        },
        middleButton: {
          borderStyle: 'hidden',
          borderRadius: 'inherit',
          margin: 2,
        },
        lastButton: {
          borderStyle: 'hidden',
          borderRadius: 'inherit',
          margin: 2,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'medium',
      },
      variants: [
        // ============================================================================
        // CONTAINED VARIANTS
        // ============================================================================

        // Contained - Primary
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.primary.main,
            },
          },
        },
        // Contained - Secondary
        {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            color: theme.palette.secondary.contrastText,
            backgroundColor: theme.palette.secondary.main,
            '&:hover': {
              backgroundColor: theme.palette.secondary.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.secondary.contrastText,
              backgroundColor: theme.palette.secondary.main,
            },
          },
        },
        // Contained - Error
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            color: theme.palette.error.contrastText,
            backgroundColor: theme.palette.error.main,
            '&:hover': {
              backgroundColor: theme.palette.error.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.error.contrastText,
              backgroundColor: theme.palette.error.main,
            },
          },
        },
        // Contained - Warning
        {
          props: { variant: 'contained', color: 'warning' },
          style: {
            color: theme.palette.warning.contrastText,
            backgroundColor: theme.palette.warning.main,
            '&:hover': {
              backgroundColor: theme.palette.warning.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.warning.contrastText,
              backgroundColor: theme.palette.warning.main,
            },
          },
        },
        // Contained - Info
        {
          props: { variant: 'contained', color: 'info' },
          style: {
            color: theme.palette.info.contrastText,
            backgroundColor: theme.palette.info.main,
            '&:hover': {
              backgroundColor: theme.palette.info.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.info.contrastText,
              backgroundColor: theme.palette.info.main,
            },
          },
        },
        // Contained - Success
        {
          props: { variant: 'contained', color: 'success' },
          style: {
            color: theme.palette.success.contrastText,
            backgroundColor: theme.palette.success.main,
            '&:hover': {
              backgroundColor: theme.palette.success.dark,
            },
            '&.Mui-disabled': {
              color: theme.palette.success.contrastText,
              backgroundColor: theme.palette.success.main,
            },
          },
        },
        // Contained - Default
        {
          props: { variant: 'contained', color: 'default' },
          style: {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.grey[300],
            '&:hover': {
              backgroundColor: theme.palette.grey[400],
            },
            '&.Mui-disabled': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.grey[300],
            },
          },
        },

        // ============================================================================
        // OUTLINED VARIANTS
        // ============================================================================

        // Outlined - Primary
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            border: '1px solid',
            color: theme.palette.primary.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.primary.main, 0.5),
            '&:hover': {
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.light,
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          },
        },
        // Outlined - Secondary
        {
          props: { variant: 'outlined', color: 'secondary' },
          style: {
            border: '1px solid',
            color: theme.palette.secondary.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.secondary.main, 0.5),
            '&:hover': {
              color: theme.palette.secondary.main,
              backgroundColor: theme.palette.secondary.light,
              borderColor: theme.palette.secondary.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.secondary.main,
              color: theme.palette.secondary.main,
            },
          },
        },
        // Outlined - Error
        {
          props: { variant: 'outlined', color: 'error' },
          style: {
            border: '1px solid',
            color: theme.palette.error.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.error.main, 0.5),
            '&:hover': {
              color: theme.palette.error.main,
              backgroundColor: theme.palette.error.light,
              borderColor: theme.palette.error.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
            },
          },
        },
        // Outlined - Warning
        {
          props: { variant: 'outlined', color: 'warning' },
          style: {
            border: '1px solid',
            color: theme.palette.warning.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.warning.main, 0.5),
            '&:hover': {
              color: theme.palette.warning.main,
              backgroundColor: theme.palette.warning.light,
              borderColor: theme.palette.warning.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.warning.main,
              color: theme.palette.warning.main,
            },
          },
        },
        // Outlined - Info
        {
          props: { variant: 'outlined', color: 'info' },
          style: {
            border: '1px solid',
            color: theme.palette.info.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.info.main, 0.5),
            '&:hover': {
              color: theme.palette.info.main,
              backgroundColor: theme.palette.info.light,
              borderColor: theme.palette.info.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.info.main,
              color: theme.palette.info.main,
            },
          },
        },
        // Outlined - Success
        {
          props: { variant: 'outlined', color: 'success' },
          style: {
            border: '1px solid',
            color: theme.palette.success.main,
            background: theme.palette.background.paper,
            borderColor: alpha(theme.palette.success.main, 0.5),
            '&:hover': {
              color: theme.palette.success.main,
              backgroundColor: theme.palette.success.light,
              borderColor: theme.palette.success.main,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.success.main,
              color: theme.palette.success.main,
            },
          },
        },
        // Outlined - Default
        {
          props: { variant: 'outlined', color: 'default' },
          style: {
            border: '1px solid',
            color: theme.palette.text.primary,
            background: theme.palette.background.paper,
            borderColor: theme.palette.border.main,
            '&:hover': {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.border.dark,
            },
            '&.Mui-disabled': {
              border: '1px solid',
              borderColor: theme.palette.border.main,
              color: theme.palette.text.primary,
            },
          },
        },

        // ============================================================================
        // TONAL VARIANTS
        // ============================================================================

        // Tonal - Primary
        {
          props: { variant: 'tonal', color: 'primary' },
          style: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.main,
            '&:hover': {
              color: theme.palette.primary.dark,
              background: theme.palette.secondary.light,
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.dark,
              },
            },
          },
        },
        // Tonal - Secondary
        {
          props: { variant: 'tonal', color: 'secondary' },
          style: {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.main,
            '&:hover': {
              color: theme.palette.secondary.main,
              backgroundColor: alpha(theme.palette.secondary.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.secondary.main,
              },
            },
          },
        },
        // Tonal - Error
        {
          props: { variant: 'tonal', color: 'error' },
          style: {
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.main,
            '&:hover': {
              color: theme.palette.error.dark,
              backgroundColor: alpha(theme.palette.error.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.error.main,
              },
            },
          },
        },
        // Tonal - Warning
        {
          props: { variant: 'tonal', color: 'warning' },
          style: {
            backgroundColor: theme.palette.warning.light,
            color: theme.palette.warning.main,
            '&:hover': {
              color: theme.palette.warning.main,
              backgroundColor: alpha(theme.palette.warning.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.warning.main,
              },
            },
          },
        },
        // Tonal - Info
        {
          props: { variant: 'tonal', color: 'info' },
          style: {
            backgroundColor: theme.palette.info.light,
            color: theme.palette.info.main,
            '&:hover': {
              color: theme.palette.info.main,
              backgroundColor: alpha(theme.palette.info.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.info.main,
              },
            },
          },
        },
        // Tonal - Success
        {
          props: { variant: 'tonal', color: 'success' },
          style: {
            backgroundColor: theme.palette.success.light,
            color: theme.palette.success.main,
            '&:hover': {
              color: theme.palette.success.main,
              backgroundColor: alpha(theme.palette.success.main, 0.4),
              '& .MuiSvgIcon-root': {
                color: theme.palette.success.main,
              },
            },
          },
        },
        // Tonal - Default
        {
          props: { variant: 'tonal', color: 'default' },
          style: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            '&:hover': {
              color: theme.palette.primary.main,
              background: theme.palette.primary.light,
            },
          },
        },

        // ============================================================================
        // SHAPE VARIANTS
        // ============================================================================

        // Size-specific rounded shapes
        {
          props: { size: 'small', shape: 'rounded' },
          style: {
            borderRadius: 6,
          },
        },
        {
          props: { size: 'medium', shape: 'rounded' },
          style: {
            borderRadius: 8,
          },
        },
        {
          props: { size: 'large', shape: 'rounded' },
          style: {
            borderRadius: 12,
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => ({
          // Icon size configurations
          '& .MuiSvgIcon-root': {
            ...(ownerState.size === 'small' && {
              fontSize: '18px',
            }),
            ...(ownerState.size === 'medium' && {
              fontSize: '20px',
            }),
            ...(ownerState.size === 'large' && {
              fontSize: '22px',
            }),
          },
          // Size-specific border radius configurations (for circular shape)
          ...(ownerState.shape !== 'rounded' &&
            ownerState.size === 'small' && {
              borderRadius: '6px',
            }),
          ...(ownerState.shape !== 'rounded' &&
            ownerState.size === 'medium' && {
              borderRadius: '8px',
            }),
          ...(ownerState.shape !== 'rounded' &&
            ownerState.size === 'large' && {
              borderRadius: '12px',
            }),
          // Circular shape (default) - use 50% for perfect circle
          ...(ownerState.shape !== 'rounded' && {
            borderRadius: '50%',
          }),
          // Fix outlined variant sizing - ensure consistent button size
          ...(ownerState.variant === 'outlined' && {
            border: '1px solid',
            padding: ownerState.size === 'small' ? '5px' : ownerState.size === 'large' ? '9px' : '7px',
          }),
          ...(ownerState.variant !== 'outlined' && {
            border: '0',
            padding: ownerState.size === 'small' ? '6px' : ownerState.size === 'large' ? '10px' : '8px',
          }),
          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
        }),
      },
    },
    MuiButton: {
      defaultProps: {
        disableRipple: true,
        size: 'medium',
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'add' },
          style: {},
        },
        {
          props: { variant: 'text' },
          style: {},
        },
        {
          props: { variant: 'contained' },
          style: {
            color: theme.palette.text.contrastText,
            '&.Mui-disabled': {
              // make disabled contained button faded main color instead of grey
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.text.contrastText,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.size === 'small' && {
            borderRadius: 6,
            ...theme.typography.paragraphMedium,
          }),
          ...(ownerState.size === 'medium' && {
            borderRadius: 8,
            ...theme.typography.paragraphMedium,
          }),
          ...(ownerState.size === 'large' && {
            borderRadius: 12,
            ...theme.typography.bodyMedium,
          }),

          '&.Mui-disabled': {
            opacity: theme.palette.action.disabledOpacity,
          },
          // Ensure custom 'tonal' variant uses same paddings as 'contained'
          ...(ownerState.variant === 'tonal' && {
            padding:
              ownerState.size === 'small'
                ? `${theme.spacing(0.5)} ${theme.spacing(1.25)}`
                : ownerState.size === 'large'
                  ? `${theme.spacing(1)} ${theme.spacing(2.75)}`
                  : `${theme.spacing(0.75)} ${theme.spacing(2)}`,
          }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color === 'primary' && {
              color: theme.palette.text.primary,
              background: theme.palette.background.paper,
              borderColor: theme.palette.border.main,
              '&:hover': {
                color: theme.palette.primary.main,
                background: theme.palette.primary.light,
                borderColor: theme.palette.border.dark,
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          ...(ownerState.variant === 'outlined' &&
            ownerState.color && {
              background: theme.palette.background.paper,
              '&:hover': {
                background:
                  theme.palette[ownerState.color as 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success']
                    ?.light || theme.palette.primary.light,
              },
            }),
          // Tonal - Primary (default)
          ...(ownerState.variant === 'tonal' &&
            (!ownerState.color || ownerState.color === 'primary') && {
              color: theme.palette.primary.main,
              background: theme.palette.primary.light,
              '&:hover': {
                color: theme.palette.primary.dark,
                background: theme.palette.secondary.light,
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Secondary
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'secondary' && {
              color: theme.palette.secondary.main,
              background: theme.palette.secondary.light,
              '&:hover': {
                color: theme.palette.secondary.dark,
                background: alpha(theme.palette.secondary.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Error
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'error' && {
              color: theme.palette.error.main,
              background: theme.palette.error.light,
              '&:hover': {
                color: theme.palette.error.dark,
                background: alpha(theme.palette.error.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Warning
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'warning' && {
              color: theme.palette.warning.main,
              background: theme.palette.warning.light,
              '&:hover': {
                color: theme.palette.warning.dark,
                background: alpha(theme.palette.warning.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Info
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'info' && {
              color: theme.palette.info.main,
              background: theme.palette.info.light,
              '&:hover': {
                color: theme.palette.info.dark,
                background: alpha(theme.palette.info.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          // Tonal - Success
          ...(ownerState.variant === 'tonal' &&
            ownerState.color === 'success' && {
              color: theme.palette.success.main,
              background: theme.palette.success.light,
              '&:hover': {
                color: theme.palette.success.dark,
                background: alpha(theme.palette.success.main, 0.4),
              },
              '&.Mui-disabled': {
                opacity: theme.palette.action.disabledOpacity,
              },
            }),
          textTransform: 'none',
          textWrap: 'nowrap',
          '@media print': {
            display: 'none',
          },
        }),
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        legend: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          height: '22px',
          borderRadius: '6px',
        },
        sizeMedium: {
          height: '28px',
          borderRadius: '8px',
        },
        labelSmall: {
          ...theme.typography.captionRegular,
        },
        label: {
          ...theme.typography.paragraphRegular,
        },
      },
      variants: [
        {
          props: { variant: 'tonal' },
          style: {},
        },
        {
          props: { variant: 'tonal', color: 'primary' },
          style: {
            '&.MuiChip-colorPrimary': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark,
            },
            '& .MuiChip-iconColorPrimary': {
              color: theme.palette.primary.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'secondary' },
          style: {
            '&.MuiChip-colorSecondary': {
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.secondary.dark,
            },
            '& .MuiChip-iconColorSecondary': {
              color: theme.palette.secondary.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'success' },
          style: {
            '&.MuiChip-colorSuccess': {
              backgroundColor: theme.palette.success.light,
              color: theme.palette.success.dark,
            },
            '& .MuiChip-iconColorSuccess': {
              color: theme.palette.success.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'info' },
          style: {
            '&.MuiChip-colorInfo': {
              backgroundColor: theme.palette.info.light,
              color: theme.palette.info.dark,
            },
            '& .MuiChip-iconColorInfo': {
              color: theme.palette.info.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'warning' },
          style: {
            '&.MuiChip-colorWarning': {
              backgroundColor: theme.palette.warning.light,
              color: theme.palette.warning.dark,
            },
            '& .MuiChip-iconColorWarning': {
              color: theme.palette.warning.dark,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'error' },
          style: {
            '&.MuiChip-colorError': {
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.dark,
            },
            '& .MuiChip-iconColorError': {
              color: theme.palette.error.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'inactive' },
          style: {
            '&.MuiChip-colorInactive': {
              backgroundColor: theme.palette.inactive.light,
              color: theme.palette.inactive.dark,
            },
            '& .MuiChip-iconColorInactive': {
              color: theme.palette.inactive.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'active' },
          style: {
            '&.MuiChip-colorActive': {
              backgroundColor: theme.palette.active.light,
              color: theme.palette.active.dark,
            },
            '& .MuiChip-iconColorActive': {
              color: theme.palette.active.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'upcoming' },
          style: {
            '&.MuiChip-colorUpcoming': {
              backgroundColor: theme.palette.upcoming.light,
              color: theme.palette.upcoming.dark,
            },
            '& .MuiChip-iconColorUpcoming': {
              color: theme.palette.upcoming.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'critical' },
          style: {
            '&.MuiChip-colorCritical': {
              backgroundColor: theme.palette.critical.light,
              color: theme.palette.critical.dark,
            },
            '& .MuiChip-iconColorCritical': {
              color: theme.palette.critical.main,
            },
          },
        },
        {
          props: { variant: 'tonal', color: 'pending' },
          style: {
            '&.MuiChip-colorPending': {
              backgroundColor: theme.palette.pending.light,
              color: theme.palette.pending.dark,
            },
            '& .MuiChip-iconColorPending': {
              color: theme.palette.pending.main,
            },
          },
        },
      ],
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          borderRadius: 12,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${theme.palette.border.light}`,
        },
        spacing: {
          padding: theme.spacing(3),
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {},
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: theme.spacing(3),
          borderBottom: `1px solid ${theme.palette.border.light}`,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        contained: {
          marginLeft: 12,
          marginRight: 0,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: {
          display: 'none',
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        input: {
          letterSpacing: '0.01071em',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        readOnly: {
          '&$readOnly': {
            background: 'none',
            border: 'none',
          },
        },
      },
      defaultProps: {
        inputProps: {
          autoCorrect: 'off',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          display: 'none',
        },
        outlined: {
          '&$shrink': {
            backgroundColor: theme.palette.mode !== 'dark' ? '#ffffff' : theme.palette.background.default,
          },
        },
      },
    },
    MuiList: {
      styleOverrides: {
        padding: {
          padding: 8,
          '& > li': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiModal: {
      defaultProps: {
        // This is because if any dialog / popover / menu is open, the user cannot type into Intercom. This has the side
        // effect of potentially losing focus for the modal, which affects accessibility. If we decide to remove this,
        // please consider all things
        disableEnforceFocus: true,
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${palette.border.light}`,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          background: theme.palette.background.default,
          border: `1px solid ${palette.border.light}`,
          // alignItems: 'baseline', // it breaks input adornments
          '&.Mui-focused': {
            background: theme.palette.background.paper,
            border: `1px solid ${palette.primary.main}`,
            boxShadow: `0px 0px 0px 4px ${palette.primary.light}`,
          },
          '&:hover': {
            background: theme.palette.background.paper,
            border: `1px solid ${palette.primary.main}`,
          },
          '&:before, &:after, &:hover:not(.Mui-disabled):before, &:hover:not(.Mui-disabled):after': {
            borderBottom: 0,
          },
          '&.Mui-disabled': {
            background: theme.palette.background.default,
            borderColor: theme.palette.border.light,
            color: theme.palette.text.disabled,
          },
          '&.Mui-error': {
            background: theme.palette.background.default,
            border: `1px solid ${palette.error.main}`,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '&:hover fieldset': {
            borderColor: theme.palette.action.hover,
          },
        },
        notchedOutline: {
          borderColor: theme.palette.border.main,
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: theme.palette.icon.main,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: {
          borderColor: theme.palette.border.light,
        },
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 12,
        },
      },
      defaultProps: {
        elevation: 8, // set to elevation 8 because right now menu/popover elevation aren't applying over the default '1' on paper
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: {
          border: `1px solid ${palette.border.light}`,
        },
      },
    },

    MuiPickersMonth: {
      styleOverrides: {
        monthButton: {
          ...theme.typography.bodyRegular,
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        yearButton: {
          ...theme.typography.bodyRegular,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        // variant: 'filled',
        IconComponent: ArrowDropDownRoundedIcon,
      },
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.background.paper,
        },
        icon: {
          color: theme.palette.icon.main,
        },
      },
    },
    MuiSkeleton: {
      defaultProps: {
        animation: 'pulse',
      },
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          backgroundColor: theme.palette.skeleton.light,
        },
        circular: {
          borderRadius: '50%',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          color: theme.palette.text.secondary,
        },
        textColorPrimary: {
          ...(palette.mode === 'dark' && {
            '&.Mui-selected': {
              color: theme.palette.primary.main,
            },
          }),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          background: theme.palette.background.paper,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'filled',
        inputProps: {
          'data-1p-ignore': 'true',
        },
      },
      styleOverrides: {
        root: {
          '&.subvariant-plain': {
            verticalAlign: 'middle',
            '& .MuiAutocomplete-endAdornment': {
              marginRight: '0px',
            },
            '& .MuiAutocomplete-inputRoot:before, .MuiAutocomplete-inputRoot:after': {
              borderBottom: 'none !important',
            },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.text.primary,
          color: theme.palette.background.paper,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.5),
          padding: theme.spacing(1),
          ...theme.typography.paragraphRegular,
        },
        arrow: {
          color: theme.palette.text.primary,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {},
      },
      defaultProps: {
        variant: 'bodyRegular',
        variantMapping: typographyVariantMappings,
      },
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          width: 40,
          height: 24,
          padding: 0,
          margin: 9,
        },
        switchBase: {
          padding: 3,
          transitionDuration: '300ms',
          '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: theme.palette.background.paper,
            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
              border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
              opacity: theme.palette.action.disabledOpacity,
              cursor: 'not-allowed',
            },
          },
          '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.primary.main,
            border: `6px solid ${palette.background.layer2}`,
          },
          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.action.disabledOpacity,
            cursor: 'not-allowed',
          },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 18,
          height: 18,
          color: theme.palette.common.white,
        },
        track: {
          borderRadius: 12,
          backgroundColor: theme.palette.background.layer2,
          opacity: 1,
          transition: theme.transitions.create(['background-color'], {
            duration: 500,
          }),
        },
      },
    },
  };
};

export default CreateMuiComponents;
